import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  Modal as AriaModal,
  ModalOverlay,
} from 'react-aria-components'
import { XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import type { PlaceDetails } from '@/types/onboarding'
import { parseGoogleAddress } from '@/lib/addressParser'
import { OnboardingInput } from './ui/OnboardingInput'

interface EditBusinessModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: EditBusinessFormData) => void
  placeDetails: PlaceDetails
  focusField?: keyof EditBusinessFormData
  businessType?: string
}

export interface EditBusinessFormData {
  addressLine1: string
  addressLine2: string
  postcode: string
  city: string
  phone: string
  website: string
  restaurantGroup: string
  useSamePhoneForEvents: boolean
}

export function EditBusinessModal({
  isOpen,
  onClose,
  onSave,
  placeDetails,
  focusField,
  businessType,
}: EditBusinessModalProps) {
  const focusRef = useRef<HTMLInputElement>(null)
  const [formData, setFormData] = useState<EditBusinessFormData>({
    addressLine1: '',
    addressLine2: '',
    postcode: '',
    city: '',
    phone: '',
    website: '',
    restaurantGroup: '',
    useSamePhoneForEvents: false,
  })

  // Initialize form when modal opens - use stored components if available, otherwise parse
  useEffect(() => {
    if (isOpen && placeDetails) {
      // If we have stored address components, use them
      if (placeDetails.addressLine1 !== undefined) {
        setFormData({
          addressLine1: placeDetails.addressLine1 || '',
          addressLine2: placeDetails.addressLine2 || '',
          postcode: placeDetails.postcode || '',
          city: placeDetails.city || '',
          phone: placeDetails.phone || '',
          website: placeDetails.website || '',
          restaurantGroup: placeDetails.restaurantGroup || '',
          useSamePhoneForEvents: placeDetails.useSamePhoneForEvents ?? false,
        })
      } else {
        // Fallback: parse address for first-time edit (from Google Places data)
        const parsed = parseGoogleAddress(placeDetails.address || '')
        
        setFormData({
          addressLine1: parsed.street,
          addressLine2: '',
          postcode: parsed.postcode,
          city: parsed.city,
          phone: placeDetails.phone || '',
          website: placeDetails.website || '',
          restaurantGroup: placeDetails.restaurantGroup || '',
          useSamePhoneForEvents: placeDetails.useSamePhoneForEvents ?? false,
        })
      }
    }
  }, [isOpen, placeDetails])

  // Auto-focus only when a specific field is requested
  useEffect(() => {
    if (isOpen && focusField) {
      const timer = setTimeout(() => {
        focusRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen, focusField])

  const handleChange = (field: keyof EditBusinessFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    onSave(formData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
      className={cn(
        'onboarding-layout fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        'bg-[rgba(10,13,18,0.72)]',
        'entering:animate-in entering:fade-in-0',
        'exiting:animate-out exiting:fade-out-0'
      )}
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <AriaModal
        className={cn(
          'w-full max-w-[512px] rounded-2xl bg-white shadow-xl outline-none',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <Dialog aria-label="Edit business information" className="outline-none">
          {/* Absorbs initial focus so no input is auto-focused on open */}
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <button type="button" autoFocus className="sr-only" aria-hidden="true" tabIndex={-1} />
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* Header */}
          <div className="relative px-6 pb-5 pt-6">
            {/* Decorative background pattern */}
            <div
              className="pointer-events-none absolute -left-[120px] -top-[120px] h-[336px] w-[336px] opacity-50"
              style={{
                background:
                  'radial-gradient(circle at center, rgba(0,0,0,0.03) 0%, transparent 70%)',
              }}
            />

            {/* Close button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClose()
              }}
              className="absolute right-3 top-3 flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-[#a4a7ae] transition-colors hover:bg-[#f5f5f5] hover:text-[#535862]"
            >
              <XCloseIcon className="h-6 w-6" />
            </button>

            {/* Title and description */}
            <div className="flex flex-col gap-0.5">
              <h2 className="text-base font-semibold leading-6 text-[#181d27]">
                Edit business information
              </h2>
              <p className="text-sm font-normal leading-5 text-[#535862]">
                Make sure everything looks right before continuing.
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-5 px-6 pb-0">
            <OnboardingInput
              ref={focusField === 'addressLine1' ? focusRef : undefined}
              label="Address line"
              type="text"
              value={formData.addressLine1}
              onChange={(e) => handleChange('addressLine1', e.target.value)}
              placeholder="Enter address"
            />

            <OnboardingInput
              label="Address line 2"
              type="text"
              value={formData.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
              placeholder="Apartment, suite, etc. (optional)"
            />

            {/* Postcode and City row */}
            <div className="flex gap-4">
              <div className="flex-1">
                <OnboardingInput
                  label="Postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => handleChange('postcode', e.target.value)}
                  placeholder="Postcode"
                />
              </div>
              <div className="flex-1">
                <OnboardingInput
                  label="City"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                />
              </div>
            </div>

            <OnboardingInput
              label="Phone number"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 (555) 000-0000"
            />

            {/* Same phone for events checkbox */}
            <label className="flex cursor-pointer items-start gap-2">
              <div className="flex shrink-0 items-center justify-center pt-[2px]">
                <input
                  type="checkbox"
                  checked={formData.useSamePhoneForEvents}
                  onChange={(e) => handleChange('useSamePhoneForEvents', e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded-[4px] border border-[#d5d7da] text-[#181d27] focus:ring-[#181d27]"
                />
              </div>
              <span className="flex-1 text-sm font-medium leading-5 text-[#414651]">
                Use the same phone number for event and group requests.
              </span>
            </label>

            <OnboardingInput
              label="Website (optional)"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="www.example.com"
            />

            <OnboardingInput
              ref={focusField === 'restaurantGroup' ? focusRef : undefined}
              label={
                businessType === 'HOTEL'
                  ? 'Hotel group name (optional)'
                  : businessType === 'SMALL_BUSINESS'
                    ? 'Company group name (optional)'
                    : 'Restaurant group name (optional)'
              }
              type="text"
              value={formData.restaurantGroup}
              onChange={(e) => handleChange('restaurantGroup', e.target.value)}
              placeholder="Enter group name"
            />
          </div>

          {/* Footer */}
          <div className="flex gap-3 px-6 pb-6 pt-8">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClose()
              }}
              className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border border-[#d5d7da] bg-white px-4 py-2.5 text-base font-semibold text-[#414651] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#f9fafb]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 border-[rgba(255,255,255,0.12)] bg-[#222] px-4 py-2.5 text-base font-semibold text-white shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#333]"
            >
              Save
            </button>
          </div>
          </form>
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  )
}
